'use client';

import { c as _c } from "react/compiler-runtime";
import { Check, Loader2 } from 'lucide-react';
import type { RouterOutputs } from '../../../utils/trpc';
import Avatar from '../../common/Avatar';
export type MoveTargetBoard = RouterOutputs['board']['moveTargets']['boards'][number];

/** The board and the list on it that cards are moved to. */
export type MoveTarget = {
  slug: string;
  listId: number;
};

/** The only board there is, with its first list: nothing to pick, so it is
 *  picked. Null when there is a choice to make. */
export function soleMoveTarget(boards: MoveTargetBoard[] | undefined): MoveTarget | null {
  const [only, ...rest] = boards ?? [];
  if (!only || rest.length > 0 || only.lists.length === 0) return null;
  return {
    slug: only.slug,
    listId: only.lists[0].id
  };
}
type Props = {
  boards: MoveTargetBoard[] | undefined;
  value: MoveTarget | null;
  onChange: (target: MoveTarget | null) => void;
};
const rowClass = (selected: boolean) => `flex w-full items-center justify-between rounded-md p-3 text-left hover:bg-background-light ${selected ? 'bg-background-secondary' : ''}`;

/** Board picker with the lists of the picked board under it. Used to move one
 *  card (MoveCardToBoardModal) and a whole list (MoveListToBoardModal). */
const BoardTargetPicker = t0 => {
  const $ = _c(19);
  const {
    boards,
    value,
    onChange
  } = t0;
  if (!boards) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex justify-center py-8"><Loader2 className="animate-spin text-textSecondary" size={24} /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (boards.length === 0) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="py-8 text-center text-sm text-textSecondary/60">You are not a member of another board.</p>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== boards || $[3] !== onChange || $[4] !== value?.listId || $[5] !== value?.slug) {
    let t2;
    if ($[7] !== value?.slug) {
      t2 = board => board.slug === value?.slug;
      $[7] = value?.slug;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    const picked = boards.find(t2);
    let t3;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <p className="mb-2 text-sm font-emphasis text-textSecondary">Board</p>;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] !== boards || $[11] !== onChange || $[12] !== value?.slug) {
      let t5;
      if ($[14] !== onChange || $[15] !== value?.slug) {
        t5 = board_0 => <button key={board_0.id} type="button" disabled={board_0.lists.length === 0} onClick={() => onChange({
          slug: board_0.slug,
          listId: board_0.lists[0].id
        })} className={`${rowClass(board_0.slug === value?.slug)} disabled:cursor-not-allowed disabled:opacity-50`}><span className="flex min-w-0 items-center gap-x-2"><Avatar size={28} spaceAvatarKey={board_0.avatarKey} /><span className="truncate">{board_0.name}</span>{board_0.lists.length === 0 && <span className="shrink-0 text-xs text-textSecondary">No lists</span>}</span>{board_0.slug === value?.slug && <Check size={18} className="shrink-0 text-primary" />}</button>;
        $[14] = onChange;
        $[15] = value?.slug;
        $[16] = t5;
      } else {
        t5 = $[16];
      }
      t4 = boards.map(t5);
      $[10] = boards;
      $[11] = onChange;
      $[12] = value?.slug;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t5;
    if ($[17] !== t4) {
      t5 = <div>{t3}<div className="max-h-[220px] space-y-1 overflow-y-auto rounded-md border border-ds-border-standard p-2">{t4}</div></div>;
      $[17] = t4;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    t1 = <div className="space-y-4">{t5}{picked && <div><p className="mb-2 text-sm font-emphasis text-textSecondary">List</p><div className="max-h-[220px] space-y-1 overflow-y-auto rounded-md border border-ds-border-standard p-2">{picked.lists.map(list => <button key={list.id} type="button" onClick={() => onChange({
            slug: picked.slug,
            listId: list.id
          })} className={rowClass(list.id === value?.listId)}><span className="truncate">{list.name}</span>{list.id === value?.listId && <Check size={18} className="shrink-0 text-primary" />}</button>)}</div></div>}</div>;
    $[2] = boards;
    $[3] = onChange;
    $[4] = value?.listId;
    $[5] = value?.slug;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
export default BoardTargetPicker;